/**
 * Admin Login Component
 * Token-protected login page for admin panel access
 * 
 * Security:
 * - Verifies token from URL query parameter
 * - Compares against VITE_ADMIN_MASTER_TOKEN from .env
 * - Stores valid token in sessionStorage
 * - Redirects to dashboard on success
 * - Shows 403 on invalid/missing token
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Admin403 } from './Admin403';

const MASTER_TOKEN = (import.meta as any).env?.VITE_ADMIN_MASTER_TOKEN || '';

export const AdminLogin = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState<string>('');
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    // Check for token in URL query parameter
    const urlToken = searchParams.get('token');
    
    if (urlToken) {
      setToken(urlToken);
      // Verify token matches master token
      if (urlToken === MASTER_TOKEN && MASTER_TOKEN) {
        setIsAuthorized(true);
        // Store token in sessionStorage
        sessionStorage.setItem('adminToken', urlToken);
        // Small delay to ensure state is set before redirect
        setTimeout(() => {
          navigate('/x9s4k-portal-72/admin', { replace: true });
        }, 100);
      } else {
        setIsAuthorized(false);
      }
    } else {
      // No token in URL - check if already logged in
      const storedToken = sessionStorage.getItem('adminToken');
      if (storedToken === MASTER_TOKEN && MASTER_TOKEN) {
        // Already authenticated, redirect to dashboard
        setTimeout(() => {
          navigate('/x9s4k-portal-72/admin', { replace: true });
        }, 100);
      } else {
        // No token and not logged in - show login form (don't set to false immediately)
        // Allow user to enter token manually
        setIsAuthorized(null);
      }
    }
  }, [searchParams, navigate]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    
    // For now, login requires token in URL
    // Future: Can add username/password authentication here
    if (token && token === MASTER_TOKEN && MASTER_TOKEN) {
      sessionStorage.setItem('adminToken', token);
      navigate('/x9s4k-portal-72/admin', { replace: true });
    } else {
      alert('Invalid token. Please access this page with a valid token in the URL.');
    }
  };

  // Show 403 if token was provided but is invalid
  if (isAuthorized === false) {
    return <Admin403 />;
  }

  // Show loading state while checking authorization (only if we have a token to check)
  if (isAuthorized === null && searchParams.get('token')) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{ color: '#ffffff', fontSize: '1.25rem' }}>Verifying access...</div>
      </div>
    );
  }

  // Debug indicator - always show "Hello" to confirm we're on the right page
  console.log('[ADMIN] AdminLogin component rendered', { 
    isAuthorized, 
    hasToken: !!searchParams.get('token'),
    url: window.location.href 
  });

  // Login form (fallback if token not in URL)
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px',
      position: 'relative'
    }}>
      {/* Debug indicator - "Hello" in the center */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: '4rem',
        fontWeight: 800,
        color: 'rgba(255, 255, 255, 0.3)',
        pointerEvents: 'none',
        zIndex: 0,
        userSelect: 'none'
      }}>
        Hello
      </div>
      
      <div style={{
        background: '#ffffff',
        padding: '40px',
        borderRadius: '20px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        maxWidth: '400px',
        width: '100%',
        position: 'relative',
        zIndex: 1
      }}>
        <h1 style={{
          fontSize: '2rem',
          fontWeight: 800,
          marginBottom: '10px',
          color: '#1a1a1a',
          textAlign: 'center'
        }}>
          Admin Login
        </h1>
        <p style={{
          color: '#6c757d',
          textAlign: 'center',
          marginBottom: '30px'
        }}>
          Gig Genie Admin Panel
        </p>

        <form onSubmit={handleLogin} style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontWeight: 600, color: '#1a1a1a' }} htmlFor="username">
              Username
            </label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={{
                padding: '12px 20px',
                border: '2px solid #e9ecef',
                borderRadius: '50px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.3s'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#667eea';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e9ecef';
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontWeight: 600, color: '#1a1a1a' }} htmlFor="password">
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                padding: '12px 20px',
                border: '2px solid #e9ecef',
                borderRadius: '50px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.3s'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#667eea';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e9ecef';
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontWeight: 600, color: '#1a1a1a' }} htmlFor="token">
              Access Token
            </label>
            <input
              type="text"
              id="token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Enter admin token"
              style={{
                padding: '12px 20px',
                border: '2px solid #e9ecef',
                borderRadius: '50px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.3s'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#667eea';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e9ecef';
              }}
            />
          </div>

          <button
            type="submit"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#ffffff',
              border: 'none',
              padding: '15px',
              borderRadius: '50px',
              fontSize: '1.125rem',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'transform 0.3s',
              marginTop: '10px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Login
          </button>
        </form>

        <p style={{
          fontSize: '0.875rem',
          color: '#6c757d',
          textAlign: 'center',
          marginTop: '20px'
        }}>
          Access requires a valid token in the URL or form above.
        </p>
      </div>
    </div>
  );
};

